import {
  ChannelRole,
  GetSignalingChannelEndpointCommand,
  KinesisVideoClient
} from '@aws-sdk/client-kinesis-video'
import {
  GetIceServerConfigCommand,
  KinesisVideoSignalingClient
} from '@aws-sdk/client-kinesis-video-signaling'
import {
  JoinStorageSessionCommand,
  KinesisVideoWebRTCStorageClient
} from '@aws-sdk/client-kinesis-video-webrtc-storage'
import { Role, SignalingClient } from 'amazon-kinesis-video-streams-webrtc'
import api from '@/services/api'

export interface KvsCredentials {
  accessKeyId: string
  secretAccessKey: string
  sessionToken: string
}

export interface KvsStreamConfig {
  channelArn: string
  credentials: KvsCredentials
  region: string
}

export interface KvsMasterHandle {
  signalingClient: SignalingClient
  close: () => Promise<void>
}

export async function getKvsCredentials(): Promise<KvsStreamConfig> {
  const { data } = await api.post('/fluency-test/video-credentials')
  return data as KvsStreamConfig
}

async function getIceServers(
  channelArn: string,
  credentials: KvsCredentials,
  region: string,
  httpsEndpoint: string
): Promise<RTCIceServer[]> {
  const signalingClient = new KinesisVideoSignalingClient({
    region,
    credentials,
    endpoint: httpsEndpoint
  })

  const response = await signalingClient.send(
    new GetIceServerConfigCommand({ ChannelARN: channelArn })
  )

  const iceServers: RTCIceServer[] = [
    { urls: `stun:stun.kinesisvideo.${region}.amazonaws.com:443` }
  ]

  for (const server of response.IceServerList ?? []) {
    if (server.Uris && server.Username && server.Password) {
      iceServers.push({
        urls: server.Uris,
        username: server.Username,
        credential: server.Password
      })
    }
  }

  return iceServers
}

export async function initKvsMasterConnection(
  config: KvsStreamConfig,
  stream: MediaStream
): Promise<KvsMasterHandle> {
  const { channelArn, credentials, region } = config

  const kvsClient = new KinesisVideoClient({
    region,
    credentials
  })

  const endpointResponse = await kvsClient.send(
    new GetSignalingChannelEndpointCommand({
      ChannelARN: channelArn,
      SingleMasterChannelEndpointConfiguration: {
        Protocols: ['WSS', 'HTTPS', 'WEBRTC'],
        Role: ChannelRole.MASTER
      }
    })
  )

  const endpointsByProtocol = (
    endpointResponse.ResourceEndpointList ?? []
  ).reduce<Record<string, string>>((acc, endpoint) => {
    if (endpoint.Protocol && endpoint.ResourceEndpoint) {
      acc[endpoint.Protocol] = endpoint.ResourceEndpoint
    }
    return acc
  }, {})

  const wssEndpoint = endpointsByProtocol['WSS']
  const httpsEndpoint = endpointsByProtocol['HTTPS']
  const webrtcEndpoint = endpointsByProtocol['WEBRTC']

  if (!wssEndpoint || !httpsEndpoint || !webrtcEndpoint) {
    throw new Error('KVS: Missing WSS, HTTPS, or WEBRTC endpoint')
  }

  const iceServers = await getIceServers(
    channelArn,
    credentials,
    region,
    httpsEndpoint
  )

  const signalingClient = new SignalingClient({
    channelARN: channelArn,
    channelEndpoint: wssEndpoint,
    role: Role.MASTER,
    region,
    credentials: {
      accessKeyId: credentials.accessKeyId,
      secretAccessKey: credentials.secretAccessKey,
      sessionToken: credentials.sessionToken
    },
    systemClockOffset: kvsClient.config.systemClockOffset
  })

  const storageClient = new KinesisVideoWebRTCStorageClient({
    region,
    credentials,
    endpoint: webrtcEndpoint
  })

  let isRemoteDescriptionSet = false

  const remoteIceCandidatesQueue: RTCIceCandidateInit[] = []
  const peerConnection = new RTCPeerConnection({ iceServers })

  // @ts-expect-error .on not in TS definitions
  signalingClient.on(
    'sdpOffer',
    async (offer: RTCSessionDescriptionInit, remoteClientId?: string) => {
      await peerConnection.setRemoteDescription(offer)
      isRemoteDescriptionSet = true

      // Add tracks AFTER setRemoteDescription (matches AWS sample pattern)
      stream.getTracks().forEach((track) => {
        peerConnection.addTrack(track, stream)
      })

      // Process any queued ICE candidates
      while (remoteIceCandidatesQueue.length > 0) {
        const candidate = remoteIceCandidatesQueue.shift()
        if (candidate) {
          await peerConnection.addIceCandidate(candidate)
        }
      }

      // Create and send answer immediately (trickle ICE — candidates sent separately)
      const localAnswer = await peerConnection.createAnswer()
      await peerConnection.setLocalDescription(localAnswer)

      const correlationId = Date.now().toString()
      signalingClient.sendSdpAnswer(
        peerConnection.localDescription!,
        remoteClientId,
        correlationId
      )
    }
  )

  // @ts-expect-error .on not in TS definitions
  signalingClient.on('iceCandidate', (candidate: RTCIceCandidateInit) => {
    if (isRemoteDescriptionSet) {
      peerConnection
        .addIceCandidate(new RTCIceCandidate(candidate))
        .catch((err) => console.error('Error adding ICE candidate:', err))
    } else {
      remoteIceCandidatesQueue.push(candidate)
    }
  })

  // Listen for error feedback from AWS on our SDP answer / ICE candidates
  // signalingClient.on('statusResponse', (statusResponse: unknown) => {
  //   console.log('KVS: statusResponse:', statusResponse)
  // })

  // Trickle ICE — send local candidates to remote via signaling
  peerConnection.addEventListener('icecandidate', (event) => {
    if (event.candidate) {
      signalingClient.sendIceCandidate(event.candidate)
    }
  })

  // peerConnection.addEventListener('connectionstatechange', () => {
  //   if (peerConnection.connectionState === 'connected') {
  //   }
  // })

  const close = async () => {
    // Stop sending media (signals end-of-stream to remote)
    for (const sender of peerConnection.getSenders()) {
      await sender.replaceTrack(null)
    }

    // Grace period for buffered frames to drain
    await new Promise((resolve) => setTimeout(resolve, 2000))

    signalingClient.close()
    peerConnection.close()
  }

  await new Promise<void>((resolve, reject) => {
    // @ts-expect-error .on not in TS definitions
    signalingClient.on('open', async () => {
      await storageClient.send(new JoinStorageSessionCommand({ channelArn }))
      resolve()
    })

    // @ts-expect-error ts definition does not export .on, but it is a valid method
    signalingClient.on('error', (error: unknown) => {
      reject(error)
    })

    // @ts-expect-error ts definition does not export .on, but it is a valid method
    signalingClient.on('close', () => {
      console.warn('KVS: signaling channel closed')
    })
    signalingClient.open()
  })

  return { signalingClient, close }
}
